import {AppBskyEmbedVideo} from '@atproto/api'
import {useQuery} from '@tanstack/react-query'

import {getVideoThumbnail} from '#/lib/media/util'
import {STALE} from '#/state/queries'
import {useAgent} from '#/state/session'
import {
  type HubCapsule,
  type HubPlay,
} from '#/screens/Hub/hubPolicy'
import {type Slide} from '#/types/capsule'

export const PLAYS_BY_USER_RQKEY = 'hubPlaysByUser'
export const ACTOR_CAPSULES_RQKEY = 'hubActorCapsules'
export const CAPSULES_BY_URIS_RQKEY = 'hubCapsulesByUris'

async function authFetch(
  agent: ReturnType<typeof useAgent>,
  nsid: string,
  opts: {
    params?: Record<string, unknown>
    init?: RequestInit
  } = {},
): Promise<unknown> {
  const accessJwt = agent.session?.accessJwt
  if (!accessJwt) throw new Error('Not authenticated')

  const base = agent.serviceUrl.toString().replace(/\/+$/, '')
  const url = new URL(`${base}/xrpc/${nsid}`)
  if (opts.params) {
    for (const [key, value] of Object.entries(opts.params)) {
      if (value === undefined) continue
      if (Array.isArray(value)) {
        for (const item of value) url.searchParams.append(key, String(item))
      } else {
        url.searchParams.set(key, String(value))
      }
    }
  }

  const res = await fetch(url.toString(), {
    ...opts.init,
    headers: {
      Authorization: `Bearer ${accessJwt}`,
      ...(opts.init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as {error?: string}).error ?? `HTTP ${res.status}`)
  }
  return res.json()
}

type RawCapsuleView = {
  uri: string
  title: string
  status: 'draft' | 'published'
  subject?: string
  level?: string
  themeId?: string
  coverImage?: string
  playCount?: number
  slideCount?: number
  indexedAt: string
  author?: {did: string; handle: string}
  post?: {uri?: string; cid?: string}
  slides?: Array<Record<string, unknown>>
}

export function normalizeCapsuleView(raw: RawCapsuleView): HubCapsule {
  const creatorDid =
    raw.author?.did ??
    (raw.uri.startsWith('at://')
      ? raw.uri.slice(5).split('/')[0]
      : '')
  return {
    uri: raw.uri,
    title: raw.title,
    status: raw.status,
    subject: raw.subject,
    level: raw.level,
    themeId: raw.themeId,
    coverImage: raw.coverImage,
    playCount: raw.playCount,
    slideCount: raw.slideCount,
    postUri: raw.post?.uri,
    indexedAt: raw.indexedAt,
    creatorDid,
    slides: (raw.slides ?? []) as Slide[],
  }
}

type RawPlay = {
  id: string
  capsuleUri: string
  score: number
  timeMs: number
  status?: 'started' | 'completed'
  slideResults?: Array<Record<string, unknown>>
  createdAt: string
}

export const createPlaysByUserQueryKey = (did: string) =>
  [PLAYS_BY_USER_RQKEY, did] as const

/**
 * The signed-in user's recent capsule plays (started + completed),
 * newest first. Powers the learner Hub rails.
 */
export function usePlaysByUserQuery() {
  const agent = useAgent()
  const did = agent.session?.did
  return useQuery({
    queryKey: createPlaysByUserQueryKey(did ?? ''),
    enabled: !!did,
    staleTime: STALE.MINUTES.ONE,
    queryFn: async (): Promise<HubPlay[]> => {
      const raw = (await authFetch(agent, 'app.bsky.feed.getPlaysByUser', {
        params: {limit: 50},
      })) as {plays?: RawPlay[]}
      return (raw.plays ?? []).map(p => ({
        id: p.id,
        capsuleUri: p.capsuleUri,
        score: p.score,
        timeMs: p.timeMs,
        // Some deployments don't return `status` yet; infer it from the
        // payload so started-but-unfinished plays keep their "continue"
        // placement instead of being dropped or mislabeled.
        status:
          p.status === 'started' || p.status === 'completed'
            ? p.status
            : p.score === 0 && p.timeMs === 0
              ? 'started'
              : 'completed',
        createdAt: p.createdAt,
      }))
    },
  })
}

export const createActorCapsulesQueryKey = (
  did: string,
  status?: 'draft' | 'published',
) => [ACTOR_CAPSULES_RQKEY, did, status ?? 'all'] as const

/**
 * Capsules authored by `actorDid`, optionally filtered by status. The
 * response is enriched server-side with playCount/recentPlays.
 */
export function useActorCapsulesQuery(
  actorDid: string | undefined,
  status?: 'draft' | 'published',
) {
  const agent = useAgent()
  return useQuery({
    queryKey: createActorCapsulesQueryKey(actorDid ?? '', status),
    enabled: !!actorDid,
    staleTime: STALE.MINUTES.ONE,
    queryFn: async (): Promise<HubCapsule[]> => {
      const raw = (await authFetch(agent, 'app.bsky.feed.getActorCapsules', {
        params: {actor: actorDid, status, limit: 50},
      })) as {capsules?: RawCapsuleView[]}
      return (raw.capsules ?? []).map(normalizeCapsuleView)
    },
  })
}

export const createCapsulesByUrisQueryKey = (uris: string[]) =>
  [CAPSULES_BY_URIS_RQKEY, uris] as const

/**
 * Bulk capsule views for a set of URIs (chunked to the endpoint's limit of
 * 25). Used to hydrate learner play rows into renderable capsules.
 */
const CAPSULE_URI_RE = /^at:\/\/([^/]+)\/app\.bsky\.feed\.capsule\//

function creatorDidOfCapsuleUri(uri: string): string | undefined {
  return CAPSULE_URI_RE.exec(uri)?.[1]
}

async function fetchCapsulesByActor(
  agent: ReturnType<typeof useAgent>,
  uris: string[],
): Promise<Record<string, HubCapsule>> {
  // Group requested URIs by their author so we can hydrate them through
  // app.bsky.feed.getActorCapsules — the capsule-batch endpoint
  // (getCapsules) is not implemented on every deployment, so this is the
  // portable fallback (and also enriches playCount/recentPlays for free).
  const byAuthor = new Map<string, string[]>()
  for (const uri of uris) {
    const did = creatorDidOfCapsuleUri(uri)
    if (!did) continue
    const list = byAuthor.get(did) ?? []
    list.push(uri)
    byAuthor.set(did, list)
  }

  const byUri: Record<string, HubCapsule> = {}
  for (const [did, wanted] of byAuthor) {
    let cursor: string | undefined
    do {
      const raw = (await authFetch(agent, 'app.bsky.feed.getActorCapsules', {
        params: {actor: did, limit: 50, cursor},
      })) as {capsules?: RawCapsuleView[]; cursor?: string}
      for (const view of raw.capsules ?? []) {
        if (wanted.includes(view.uri)) {
          byUri[view.uri] = normalizeCapsuleView(view)
        }
      }
      cursor = raw.cursor
    } while (cursor && wanted.some(uri => !byUri[uri]))
  }
  return byUri
}

export function useCapsulesByUrisQuery(uris: string[]) {
  const agent = useAgent()
  const unique = [...new Set(uris)].sort()
  return useQuery({
    queryKey: createCapsulesByUrisQueryKey(unique),
    enabled: unique.length > 0,
    staleTime: STALE.MINUTES.FIVE,
    queryFn: async (): Promise<Record<string, HubCapsule>> => {
      try {
        // Fast path: batch fetch by URI where the endpoint is deployed.
        const chunks: RawCapsuleView[][] = []
        for (let i = 0; i < unique.length; i += 25) {
          const chunk = unique.slice(i, i + 25)
          const raw = (await authFetch(agent, 'app.bsky.feed.getCapsules', {
            params: {uris: chunk},
          })) as {capsules?: RawCapsuleView[]}
          chunks.push(raw.capsules ?? [])
        }
        const byUri: Record<string, HubCapsule> = {}
        for (const raw of chunks.flat()) {
          byUri[raw.uri] = normalizeCapsuleView(raw)
        }
        return byUri
      } catch (e) {
        // Fall back to per-author capsule listing on deployments without
        // app.bsky.feed.getCapsules (e.g. the current production appview).
        console.warn(
          'getCapsules unavailable, hydrating via getActorCapsules',
          e,
        )
        return fetchCapsulesByActor(agent, unique)
      }
    },
  })
}

export const POST_THUMBNAILS_RQKEY = 'hubPostThumbnails'
export const createPostThumbnailsQueryKey = (uris: string[]) =>
  [POST_THUMBNAILS_RQKEY, uris] as const

async function fetchPostThumbnails(
  agent: ReturnType<typeof useAgent>,
  uris: string[],
): Promise<Record<string, string>> {
  const byUri: Record<string, string> = {}
  for (let i = 0; i < uris.length; i += 25) {
    const chunk = uris.slice(i, i + 25)
    const res = await agent.app.bsky.feed.getPosts({uris: chunk})
    for (const post of res.data.posts ?? []) {
      if (!AppBskyEmbedVideo.isView(post.embed)) continue
      const thumb = getVideoThumbnail(post.embed)
      if (thumb) byUri[post.uri] = thumb
    }
  }
  return byUri
}

/**
 * Video thumbnail (repairing the truncated `.jp` extension) for each of the
 * given capsule video-post URIs, so hub tiles can show the real capsule art
 * instead of a placeholder. Empty when a post has no video embed.
 */
export function usePostThumbnailsQuery(uris: string[]) {
  const agent = useAgent()
  const unique = [...new Set(uris.filter(Boolean))].sort()
  return useQuery({
    queryKey: createPostThumbnailsQueryKey(unique),
    enabled: unique.length > 0,
    staleTime: STALE.MINUTES.FIVE,
    queryFn: () => fetchPostThumbnails(agent, unique),
  })
}
